import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Activity, FileText, ClipboardList, 
  Users, Sparkles, ChevronRight, GraduationCap,
  ExternalLink, Layers, Puzzle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LegalFooter } from '../components/LegalFooter';

export function LandingPage() {
  const products = [
    {
      id: 'frame-interventions',
      name: 'Frame Interventions',
      description: 'Pre-referral and intervention tracking for schools. One organized place to document supports, monitor progress, assign responsibilities, and make better decisions before special education referral.',
      icon: Activity,
      color: '#2C4C3B',
      bgColor: 'bg-[#2C4C3B]/10',
      features: ['Tier 1, 2, and 3 intervention plans', 'Baseline, current performance, and goals', 'Progress monitoring over time', 'Provider assignments and review dates', 'Students needing attention', 'Intervention and progress reports'],
      status: 'live',
      statusLabel: 'Live',
      href: 'https://interventions.frameed.com/login',
      buttonLabel: 'Launch Frame Interventions',
    },
    {
      id: 'frame-practice',
      name: 'Frame Practice',
      description: 'IEP implementation, goal development, service tracking, and provider coordination. Move beyond the written plan and track what is actually happening across student services, goals, supports, and progress.',
      icon: ClipboardList,
      color: '#3A5A3A',
      bgColor: 'bg-[#3A5A3A]/10',
      features: ['Measurable IEP goal development', 'Goal progress and data tracking', 'Service scheduling across providers', 'Service minutes and fidelity', 'Supports and accommodations tracking', 'Administrator visibility into implementation'],
      status: 'soon',
      statusLabel: 'Launching Soon',
      href: null,
      buttonLabel: 'Coming Soon',
    },
    {
      id: 'frame-iep-met',
      name: 'Frame IEP/MET',
      description: 'Case management and documentation tools for IEP and MET workflows, with AI-assisted drafting, Arizona-specific compliance support, and connected student information over time.',
      icon: FileText,
      color: '#3B6A9B',
      bgColor: 'bg-[#3B6A9B]/10',
      features: ['IEP workflow and document drafting', 'MET workflow and eligibility', 'PWN drafting', 'Present levels and goals', 'Arizona-specific compliance support'],
      status: 'dev',
      statusLabel: 'In Development',
      href: null,
      buttonLabel: 'Coming Soon',
    },
  ];

  const ecosystemFeatures = [
    {
      icon: Layers,
      title: 'Modular by Design',
      description: 'Start with the product you need today. Add more as your team grows — each product is built to stand alone or work together.',
    },
    {
      icon: Users,
      title: 'Built for Teams',
      description: 'Designed for schools, therapists, and special education teams with real workflows in mind.',
    },
    {
      icon: Sparkles,
      title: 'AI-Powered',
      description: 'Intelligent drafting, suggestions, and summaries built into every product to save time and improve quality.',
    },
    {
      icon: Puzzle,
      title: 'Growing Ecosystem',
      description: 'New products and deeper integrations are always in development. The FrameEd platform evolves with your needs.',
    },
  ];

  const scrollToProducts = () => {
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <GraduationCap className="h-8 w-8 text-[#2C4C3B]" />
              <span className="font-outfit text-2xl font-semibold text-[#1C1C1C]">FrameEd</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={scrollToProducts}
                className="border-[#E5E5E5] text-[#595959] hover:text-[#1C1C1C] hover:border-[#2C4C3B] rounded-xl"
                data-testid="nav-explore-btn"
              >
                Explore Products
              </Button>
              <a href="https://interventions.frameed.com/login" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl" data-testid="nav-launch-btn">
                  Launch Frame Interventions
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/039b4e73-a602-4202-ac7a-82ca5d9fd25c/images/931e2859eedeb93ca67e6a32e2d6a2eba198a4aeb6067c8fec0056e4bc771f40.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-[#1C1C1C] mb-6 animate-fadeInUp">
              Student Support,{' '}
              <span className="text-[#2C4C3B]">Unified</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#595959] leading-relaxed mb-6 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
              A modular ecosystem for schools, therapists, and special education teams. 
              Start with Frame Interventions today, with Frame Practice and Frame IEP/MET coming soon.
            </p>
            <p className="text-base text-[#7A7A7A] leading-relaxed mb-10 max-w-2xl mx-auto animate-fadeInUp" style={{animationDelay: '0.15s'}}>
              Replace the spreadsheets, paper forms, shared drives, and disconnected systems with a clear, connected workspace where student support is organized and unified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              <a href="https://interventions.frameed.com/login" target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="hero-launch-btn"
                  size="lg" 
                  className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl px-8 py-6 text-lg"
                >
                  Launch Frame Interventions
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Button 
                data-testid="hero-explore-btn"
                variant="outline" 
                size="lg" 
                className="border-[#E5E5E5] text-[#1C1C1C] hover:border-[#2C4C3B] rounded-xl px-8 py-6 text-lg"
                onClick={scrollToProducts}
              >
                Explore Products
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FrameEd Products Section */}
      <section id="products-section" className="py-24 bg-[#F3F4F1]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-[#1C1C1C] mb-4">
              FrameEd Products
            </h2>
            <p className="text-[#595959] text-lg max-w-2xl mx-auto">
              Explore the main FrameEd product ecosystem. Launch available products now and preview what is coming next.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="bg-white border border-[#E5E5E5] rounded-3xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col"
              >
                <div className={`w-14 h-14 rounded-2xl ${product.bgColor} flex items-center justify-center mb-6`}>
                  <product.icon className="h-7 w-7" style={{color: product.color}} />
                </div>
                <div className="mb-4">
                  {product.status === 'live' && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-[#2C4C3B]/10 text-[#2C4C3B]" data-testid={`status-${product.id}`}>
                      {product.statusLabel}
                    </span>
                  )}
                  {product.status === 'soon' && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-amber-50 text-amber-700 border border-amber-200" data-testid={`status-${product.id}`}>
                      {product.statusLabel}
                    </span>
                  )}
                  {product.status === 'dev' && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-[#3B6A9B]/10 text-[#3B6A9B]" data-testid={`status-${product.id}`}>
                      {product.statusLabel}
                    </span>
                  )}
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#1C1C1C] mb-3">{product.name}</h3>
                <p className="text-[#595959] text-sm leading-relaxed mb-6">{product.description}</p>
                <div className="space-y-2 mb-8">
                  {product.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-[#595959]">
                      <ChevronRight className="h-4 w-4 text-[#2C4C3B] shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
                <div className="mt-auto">
                  {product.href ? (
                    <a href={product.href} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl" data-testid={`launch-${product.id}`}>
                        {product.buttonLabel}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  ) : (
                    <Button className="w-full bg-[#E5E5E5] text-[#595959] rounded-xl cursor-default hover:bg-[#E5E5E5]" disabled data-testid={`launch-${product.id}`}>
                      {product.buttonLabel}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One Ecosystem, Many Solutions */}
      <section className="py-24 bg-white" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-[#1C1C1C] mb-4">
              One Ecosystem, Many Solutions
            </h2>
            <p className="text-[#595959] text-lg max-w-2xl mx-auto">
              FrameEd products are designed to work independently today and connect more deeply over time.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosystemFeatures.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-[#FDFCFB] border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="w-12 h-12 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-[#2C4C3B]" />
                </div>
                <h3 className="font-outfit text-lg font-medium text-[#1C1C1C] mb-2">{feature.title}</h3>
                <p className="text-[#595959] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#2C4C3B]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready to get started?
          </h2>
          <p className="text-[#C2D3C0] text-lg mb-8">
            Frame Interventions is live now. Launch it today and start building better outcomes for your students.
          </p>
          <a href="https://interventions.frameed.com/login" target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="cta-launch-btn"
              size="lg" 
              className="bg-white hover:bg-[#F3F4F1] text-[#2C4C3B] rounded-xl px-8 py-6 text-lg"
            >
              Launch Frame Interventions
              <ExternalLink className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
